import { StringTMap } from '@@types/common';

import { requestIdleCallback } from '~/components/Util/idleCallbackUtil';
import { HOUR } from '~/constants/date';
import { action, observable } from 'mobx';

type GetIdMap = () => StringTMap<true>;

class DataSuper<T extends object> {
  @observable.shallow
  private data: StringTMap<T> = {};

  private refCount: StringTMap<StringTMap<boolean>> = {};

  private getIdMapList: GetIdMap[] = [];

  private gcId: number | null;

  private index: number = 0;

  public initInStore = (getIdMap: GetIdMap) => {
    this.addGetIdMap(getIdMap);

    return {
      getInStore: (id: string) => this.getById(id),
      gc: () => this.startGc(),
    };
  };

  public getInStore = (getIdMap: GetIdMap) => {
    this.addGetIdMap(getIdMap);
    return (id: string) => this.getById(id);
  };

  public getWithDisposer = (id: string): T & { dispose: () => void } => {
    const key = `${this.index}`;
    this.index += 1;

    this.refCount[id] ??= {};
    this.refCount[id][key] = true;

    return Object.assign(this.getById(id), {
      dispose: () => {
        this.refCount[id] ??= {};
        delete this.refCount[id][key];

        this.startGc();
      },
    });
  };

  public removeInStore = (getIdMap: GetIdMap) => {
    this.addGetIdMap(getIdMap);
    return () => this.startGc();
  };

  @action
  public setData = (id: string, data: T) => {
    this.data[id] = data;
  };

  @action
  public setDataMulti = (idList: string[], dataList: T[]) => {
    dataList.forEach((data, index) => {
      this.data[idList[index]] = data;
    });
  };

  @action
  public setDataMap = (dataMap: StringTMap<T>) => {
    this.data = Object.assign({}, this.data, dataMap);
  };

  private getById = (id: string) => {
    return this.data[id];
  };

  private addGetIdMap = (getIdMap: GetIdMap) => {
    const index = this.getIdMapList.indexOf(getIdMap);
    if (index === -1) {
      this.getIdMapList.push(getIdMap);
    }
  };

  private startGc = () => {
    if (this.gcId) {
      return;
    }
    this.gcId = requestIdleCallback(
      () => {
        this.gc();
        this.gcId = null;
      },
      { timeout: HOUR }
    );
  };

  @action
  private gc = () => {
    const keyMap = this.getIdMapList.reduce((acc, get) => {
      return Object.assign(acc, get());
    }, {});

    const ids = Object.keys(this.data);
    ids.forEach((id) => {
      if (!keyMap[id] && (!this.refCount[id] || Object.keys(this.refCount[id]).length === 0)) {
        delete this.refCount[id];
        delete this.data[id];
      }
    });
  };
}

export default DataSuper;
